import React, { useEffect, useMemo, useState } from 'react';
import { Building2, ChevronRight, LogOut, Search, ShieldCheck, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { createVenueOwnerSession, getVenueOwnersForAdmin } from '../services/authService';

export default function SelectVenueOwner() {
  const navigate = useNavigate();
  const { user, loginAsVenueOwner, logout } = useAuth();
  const [owners, setOwners] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getVenueOwnersForAdmin();
        setOwners(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load venue owners');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredOwners = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return owners;
    return owners.filter((owner) => [
      owner.name,
      owner.email,
      ...(owner.venues || []).flatMap((venue) => [venue.name, venue.city]),
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [owners, search]);

  async function selectOwner(owner) {
    setSelectingId(owner.id);
    try {
      const data = await createVenueOwnerSession(owner.id);
      loginAsVenueOwner(data.token, {
        id: data.userId,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email,
        role: data.role,
        actingAdminId: data.actingAdminId,
        isAdminDelegatedSession: data.isAdminDelegatedSession,
      });
      toast.success(`Viewing the portal as ${owner.name || owner.email}`);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to start owner session');
    } finally {
      setSelectingId(null);
    }
  }

  function signOut() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <main className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="flex flex-col gap-5 rounded-[20px] bg-[#264D59] p-6 text-white shadow-[0_8px_24px_rgba(38,77,89,.18)] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F9E07F] text-[#264D59]">
              <ShieldCheck size={25} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/60">Administrator access</p>
              <h1 className="mt-1 text-2xl font-bold">Select a venue owner</h1>
              <p className="mt-1 text-sm text-white/70">Choose whose Court Owner Portal you want to manage.</p>
            </div>
          </div>
          <button type="button" onClick={signOut} className="inline-flex h-10 items-center justify-center rounded-xl border border-white/20 px-4 text-sm font-semibold text-white hover:bg-white/10">
            <LogOut size={16} className="mr-2" /> Sign out
          </button>
        </header>

        <section className="card p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold text-[#1F2937]">Venue owners</h2>
              <p className="mt-1 text-sm text-[#64748B]">
                Signed in as {user?.email}. The selected owner will be clearly shown in the portal.
              </p>
            </div>
            <div className="relative sm:w-80">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="input-field !h-11 !pl-11"
                placeholder="Search owner or venue"
                aria-label="Search venue owners"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
            </div>
          ) : filteredOwners.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="mx-auto h-10 w-10 text-[#94A3B8]" />
              <p className="mt-3 font-medium text-[#1F2937]">No venue owners found</p>
              <p className="mt-1 text-sm text-[#64748B]">Try another search or create an active Court Owner account first.</p>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {filteredOwners.map((owner) => (
                <button
                  type="button"
                  key={owner.id}
                  onClick={() => selectOwner(owner)}
                  disabled={Boolean(selectingId)}
                  className="group rounded-[18px] border border-[#E2E8F0] bg-white p-5 text-left transition-all hover:border-[#56B7AC] hover:shadow-[0_6px_18px_rgba(0,0,0,.06)] disabled:cursor-wait disabled:opacity-60"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E0F7F4] text-sm font-bold text-[#264D59]">
                      {(owner.name || owner.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-[#1F2937]">{owner.name || 'Unnamed owner'}</p>
                          <p className="truncate text-sm text-[#64748B]">{owner.email}</p>
                        </div>
                        {selectingId === owner.id ? (
                          <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-[#264D59] border-t-transparent" />
                        ) : (
                          <ChevronRight className="shrink-0 text-[#94A3B8] transition-transform group-hover:translate-x-1 group-hover:text-[#264D59]" size={20} />
                        )}
                      </div>

                      <div className="mt-4 border-t border-[#E2E8F0] pt-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#64748B]">
                          <Building2 size={14} /> {owner.venues?.length || 0} venue{owner.venues?.length === 1 ? '' : 's'}
                        </p>
                        {owner.venues?.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {owner.venues.slice(0, 3).map((venue) => (
                              <span key={venue.id} className="chip !px-2.5 !py-1 !text-xs">{venue.name}</span>
                            ))}
                            {owner.venues.length > 3 && <span className="chip !px-2.5 !py-1 !text-xs">+{owner.venues.length - 3} more</span>}
                          </div>
                        ) : (
                          <p className="mt-2 text-xs text-[#94A3B8]">No venues yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
